import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"NUXT_ENV_FALLBACK_LOGO":"@/assets/img/PB_Logo.svg","NUXT_ENV_FALLBACK_LOADING_PAGE_IMG":"@/assets/img/loading-page-img.svg","NUXT_ENV_FALLBACK_LOGO_ORG":"@/assets/img/PB_Logo_Org.svg","NUXT_ENV_FALLBACK_LOGO_WIDE":"@/assets/img/PB_Logo_Wide.svg","NUXT_ENV_FALLBACK_IMAGE":"@/assets/img/fallback-card-01.jpg","NUXT_ENV_FALLBACK_CARD_IMAGE":"@/assets/img/fallback-card-01.jpg","NUXT_ENV_FALLBACK_MEDIUM_HERO_IMAGE":"@/assets/img/fallback-medium-hero-01.jpg","NUXT_ENV_FALLBACK_SMALL_HERO_IMAGE":"@/assets/img/fallback-small-hero-01.jpg","NUXT_ENV_FALLBACK_IMAGE_STATIC":"/fallback-card-01.jpg","NUXT_ENV_FALLBACK_CARD_IMAGE_STATIC":"/fallback-card-01.jpg","NUXT_ENV_FALLBACK_MEDIUM_HERO_IMAGE_STATIC":"/fallback-medium-hero-01.jpg","NUXT_ENV_FALLBACK_SMALL_HERO_IMAGE_STATIC":"/fallback-small-hero-01.jpg","NUXT_ENV_STRAPI_API_ACTIVITIES_ENDPOINT":"/activities","NUXT_ENV_STRAPI_API_ARTICLES_ENDPOINT":"/articles","NUXT_ENV_STRAPI_API_ARTICLE_CATEGORIES_ENDPOINT":"/article-categories","NUXT_ENV_STRAPI_API_CITIES_ENDPOINT":"/cities","NUXT_ENV_STRAPI_API_EVENTS_ENDPOINT":"/events","NUXT_ENV_STRAPI_API_EVENT_CATEGORIES_ENDPOINT":"/event-categories","NUXT_ENV_STRAPI_API_PAGES_ENDPOINT":"/pages","NUXT_ENV_STRAPI_API_WIKIS_ENDPOINT":"/wikis","NUXT_ENV_OAUTH_REDIRECT_URL":"https://www.promotionbasis.de/oauth","NUXT_ENV_API_DOMAIN":"https://www.promotionbasis.de/api","NUXT_ENV_API_LOGIN_ENDPOINT":"/login","NUXT_ENV_API_LOGIN_APPLE_ENDPOINT":"/oauth/login/apple","NUXT_ENV_API_REGISTER_APPLE_ENDPOINT":"/oauth/register/apple","NUXT_ENV_API_LOGIN_FACEBOOK_ENDPOINT":"/oauth/login/facebook","NUXT_ENV_API_REGISTER_FACEBOOK_ENDPOINT":"/oauth/register/facebook","NUXT_ENV_API_LOGIN_GOOGLE_ENDPOINT":"/oauth/login/google","NUXT_ENV_API_REGISTER_GOOGLE_ENDPOINT":"/oauth/register/google","NUXT_ENV_API_LOGIN_MICROSOFT_ENDPOINT":"/oauth/login/microsoft","NUXT_ENV_API_REGISTER_MICROSOFT_ENDPOINT":"/oauth/register/microsoft","NUXT_ENV_API_REFRESH_ENDPOINT":"/token/refresh","NUXT_ENV_API_LOGOUT_ENDPOINT":"/logout","NUXT_ENV_API_ACCOUNT_ME_ENDPOINT":"/candidate_accounts/me","NUXT_ENV_API_ACCOUNT_ENDPOINT":"/candidate_accounts/$id","NUXT_ENV_API_DELETE_ACCOUNT_ENDPOINT":"/candidate_accounts/$id/delete","NUXT_ENV_API_ACCOUNT_MAUTIC_ID_ENDPOINT":"/candidate_accounts/$id/mautic_id","NUXT_ENV_API_VERIFY_USER_ACCOUNT":"/candidate_accounts_request_email_verification","NUXT_ENV_API_FORGOT_PASSWORD_EMAIL":"/candidate_accounts_request_password_reset?email=$email","NUXT_ENV_API_SET_NEW_PASSWORD":"/candidate_accounts/$id/password","NUXT_ENV_API_RESET_PASSWORD_VALIDATE_TOKEN":"/candidate_accounts/validate_password_reset_token/$token","NUXT_ENV_API_BYLL_CREATE_ACCOUNT":"/candidate_accounts/$id/create_byll_account","NUXT_ENV_API_BYLL_OPEN_ACCOUNT":"/candidate_accounts/$id/byll_login","NUXT_ENV_API_CHANGE_PASSWORD":"/candidate_accounts/$id/change_password","NUXT_ENV_API_CHANGE_EMAIL":"/candidate_accounts/$id/change_email","NUXT_ENV_API_VALIDATE_EMAIL":"/candidate_accounts/validate_email_verification_token/$token","NUXT_ENV_API_PBCOINS_DASHBOARD":"/candidate_account/$id/pb_coins/dashboard","NUXT_ENV_API_NUMLOGINS_DASHBOARD":"/candidate_account/$id/pb_coins/num_logins","NUXT_ENV_API_LASTLOGIN_DASHBOARD":"/candidate_account/$id/pb_coins/last_login","NUXT_ENV_API_STAR_RATINGS":"/candidate_accounts/$id/star_ratings","NUXT_ENV_API_PROFILE_MASTER_DATA_ENDPOINT":"/candidate_profiles/$id","NUXT_ENV_API_PROFILE_SEDCARD_ENDPOINT":"/candidate_profiles/$id/sedcard","NUXT_ENV_API_PROFILE_REGISTRATION_ENDPOINT":"/candidate_profiles/register","NUXT_ENV_API_PROFILE_REGISTER_AND_APPLY_ENDPOINT":"/candidate_profiles/register_and_apply_on_job","NUXT_ENV_API_PROFILE_PBASIS_SEARCH_STATUS":"/candidate_profiles/$candidate_profile_id/pbasis_search/$organization_id/status","NUXT_ENV_API_PROFILE_EMAIL_CHECK_ENDPOINT":"/candidate_profiles/email_check","NUXT_ENV_API_PROFILE_REGISTER_WITHOUT_PASSWORD_ENDPOINT":"/candidate_profiles/register_without_password","NUXT_ENV_API_PROFILE_DOCUMENTS":"/profile_documents/$id","NUXT_ENV_API_PROFILE_DOCUMENTS_MISC":"/profile_documents_misc","NUXT_ENV_API_PROFILE_DOCUMENTS_BUSINESS_LICENSE":"/profile_documents_business_license","NUXT_ENV_API_PROFILE_DOCUMENTS_ENROLLMENT_CERTIFICATE":"/profile_documents_certificate_of_enrollment","NUXT_ENV_API_PROFILE_DOCUMENTS_PROFILE_DOCUMENT_ATTACHMENTS":"/profile_documents/$id/profile_document_attachments","NUXT_ENV_API_PROFILE_DOCUMENT_ATTACHMENTS":"/profile_document_attachments/$id","NUXT_ENV_API_PROFILE_IMAGES_ENDPOINT":"/profile_images/$id","NUXT_ENV_API_PROFILE_IMAGES_FILE_ENDPOINT":"/profile_images/file/$id","NUXT_ENV_API_PROFILE_IMAGES_AMERICAN_PORTRAIT_ENDPOINT":"/candidate_profiles/$id/profile_images_american_portrait","NUXT_ENV_API_PROFILE_IMAGES_FULL_BODY_ENDPOINT":"/candidate_profiles/$id/profile_images_full_body","NUXT_ENV_API_PROFILE_IMAGES_MISC_ENDPOINT":"/candidate_profiles/$id/profile_images_misc","NUXT_ENV_API_PROFILE_IMAGES_PORTRAIT_ENDPOINT":"/candidate_profiles/$id/profile_images_portrait","NUXT_ENV_API_PROFILE_VIDEOS_DELETE_ENDPOINT":"/profile_videos/$id","NUXT_ENV_API_PROFILE_VIDEOS_DOWNLOAD_ENDPOINT":"/profile_videos/file/$id","NUXT_ENV_API_PROFILE_VIDEOS_GOALS_ENDPOINT":"/candidate_profiles/$id/profile_videos_goals","NUXT_ENV_API_PROFILE_VIDEOS_INTRODUCTION_ENDPOINT":"/candidate_profiles/$id/profile_videos_introduction","NUXT_ENV_API_PROFILE_VIDEOS_SELLING_POINT_ENDPOINT":"/candidate_profiles/$id/profile_videos_selling_point","NUXT_ENV_API_JOB_POSTING_ENDPOINT":"/job_postings/$id","NUXT_ENV_API_JOB_POSTING_BY_SLUG_ENDPOINT":"/job_postings/slug/$slug","NUXT_ENV_API_JOB_POSTINGS_ENDPOINT":"/job_postings","NUXT_ENV_API_JOB_POSTINGS_ACTIVE_KEYS_ENDPOINT":"/job_postings_active_keys","NUXT_ENV_API_JOB_POSTINGS_TOTAL_ENDPOINT":"/job_postings/total","NUXT_ENV_API_PROMOTIONJOBS_TOTAL_ENDPOINT":"/job_postings/total/promotionjob","NUXT_ENV_API_EVENTJOBS_TOTAL_ENDPOINT":"/job_postings/total/eventjob","NUXT_ENV_API_FERIENJOBS_TOTAL_ENDPOINT":"/job_postings/total/ferienjob","NUXT_ENV_API_GASTROJOBS_TOTAL_ENDPOINT":"/job_postings/total/gastrojob","NUXT_ENV_API_MESSEJOBS_TOTAL_ENDPOINT":"/job_postings/total/messejob","NUXT_ENV_API_NEBENJOBS_TOTAL_ENDPOINT":"/job_postings/total/nebenjob","NUXT_ENV_API_RENTERJOBS_TOTAL_ENDPOINT":"/job_postings/total/rentnerjob","NUXT_ENV_API_SCHUELERJOBS_TOTAL_ENDPOINT":"/job_postings/total/schuelerjob","NUXT_ENV_API_STUDENTENJOBS_TOTAL_ENDPOINT":"/job_postings/total/studentenjob","NUXT_ENV_API_JOB_POSTING_RECOMMEND_ENDPOINT":"/job_postings/$id/recommend/$numberJobs/random","NUXT_ENV_API_JOB_POSTING_LOGO":"/job_postings/$id/logo","NUXT_ENV_API_JOB_ALERT_CREATE_ENDPOINT":"/job_alerts","NUXT_ENV_API_JOB_ALERT_GET_ENDPOINT":"/candidate_accounts/$id/job_alerts","NUXT_ENV_API_JOB_ALERT_DELETE_ENDPOINT":"/job_alerts/$id/delete","NUXT_ENV_API_JOB_ALERT_CONFIRM_ENDPOINT":"/job_alerts/$id/confirm","NUXT_ENV_API_JOB_ALERT_PAUSE_ENDPOINT":"/job_alerts/$id/pause","NUXT_ENV_API_JOB_ALERT_UNPAUSE_ENDPOINT":"/job_alerts/$id/unpause","NUXT_ENV_API_JOB_ALERT_UPDATE_ENDPOINT":"/job_alerts/$id","NUXT_ENV_API_LEGACY_BILLING_ENDPOINT":"/legacy_billing","NUXT_ENV_API_LEGACY_BILLING_PRINT_ENDPOINT":"/legacy_billing/print/$id","NUXT_ENV_API_ORGANIZATION_ENDPOINT":"/organizations/$id","NUXT_ENV_API_ORGANIZATIONS_ENDPOINT":"/organizations","NUXT_ENV_API_ORGANIZATION_CONTACTED_FROM_USER":"/organizations/$id/contact/user","NUXT_ENV_API_ORGANIZATION_CONTACTED_FROM_VISITOR":"/organizations/$id/contact/visitor","NUXT_ENV_API_ORGANIZATION_CARDS_ENDPOINT":"/organization_cards","NUXT_ENV_API_ORGANIZATION_CARD_ENDPOINT":"/organization_cards/$id","NUXT_ENV_API_ORGANIZATION_REGISTRATION_ENDPOINT":"/organization_member/register","NUXT_ENV_API_CONTACT_ABOUT_PROJECT":"/project_contact","NUXT_ENV_API_GENERAL_CONTACT":"/general_contact","NUXT_ENV_API_ORGANIZATION_STAR_RATINGS":"/organizations/$id/star_ratings","NUXT_ENV_API_ORGANIZATION_GIVE_STAR":"/organization_star_ratings","NUXT_ENV_API_ORGANIZATION_REMOVE_STAR":"/organization_star_ratings/$id","NUXT_ENV_API_APPLICATION_APPLY_ON_JOB_POSTING_ENDPOINT":"/job_postings/$id/apply","NUXT_ENV_API_APPLICATION_MY_APPLICATIONS_ENDPOINT":"/applications/my","NUXT_ENV_API_FAVORITE_FAVOR_JOB_POSTING_ENDPOINT":"/job_postings/$id/favor","NUXT_ENV_API_FAVORITE_UNFAVOR_JOB_POSTING_ENDPOINT":"/job_postings/$id/unfavor","NUXT_ENV_API_JOB_POSTING_AUTOMATIC_FAVOR_ENDPOINT":"/job_postings/$id/auto_favor","NUXT_ENV_API_FAVORITE_MY_FAVORITES_ENDPOINT":"/favorites/my","NUXT_ENV_API_WEBHOOK_ARTICLE_DETAILS_VIEWED_ENDPOINT":"/webhook/article_details_viewed","NUXT_ENV_API_WEBHOOK_ARTICLE_LISTING_VIEWED_ENDPOINT":"/webhook/article_listing_viewed","NUXT_ENV_API_WEBHOOK_BYLL_ACCOUNT_CREATED_ENDPOINT":"/webhook/byll_account_created","NUXT_ENV_API_WEBHOOK_BYLL_USED_ENDPOINT":"/webhook/byll_used","NUXT_ENV_API_WEBHOOK_EMAIL_VERIFIED_ENDPOINT":"/webhook/email_verified","NUXT_ENV_API_WEBHOOK_JOBS_SEARCHED_ENDPOINT":"/webhook/jobs_searched","NUXT_ENV_API_WEBHOOK_JOBS_SHARED_ENDPOINT":"/webhook/job_shared","NUXT_ENV_API_WEBHOOK_ARTICLES_SHARED_ENDPOINT":"/webhook/article_shared","NUXT_ENV_API_WEBHOOK_EVENTS_SHARED_ENDPOINT":"/webhook/event_shared","NUXT_ENV_API_WEBHOOK_WIKIS_SHARED_ENDPOINT":"/webhook/wiki_shared","NUXT_ENV_API_WEBHOOK_JOB_APPLIED_TO_ENDPOINT":"/webhook/job_applied_to","NUXT_ENV_API_WEBHOOK_JOB_BOX_VIEWED_ENDPOINT":"/webhook/job_box_viewed","NUXT_ENV_API_WEBHOOK_JOB_DETAILS_CLICKED_ENDPOINT":"/webhook/job_details_clicked","NUXT_ENV_API_WEBHOOK_JOB_APPLICATION_CLICKED_ENDPOINT":"/webhook/job_application_clicked","NUXT_ENV_API_WEBHOOK_JOB_DETAILS_VIEWED_ENDPOINT":"/webhook/job_details_viewed","NUXT_ENV_API_WEBHOOK_JOB_FAVORED_ENDPOINT":"/webhook/job_favored","NUXT_ENV_API_WEBHOOK_JOB_LISTING_VIEWED_ENDPOINT":"/webhook/job_listing_viewed","NUXT_ENV_API_WEBHOOK_JOB_UNFAVORED_ENDPOINT":"/webhook/job_unfavored","NUXT_ENV_API_WEBHOOK_LOGGED_IN_ENDPOINT":"/webhook/logged_in","NUXT_ENV_API_WEBHOOK_NEWSLETTER_SETTING_CHANGED_ENDPOINT":"/webhook/newsletter_setting_changed","NUXT_ENV_API_WEBHOOK_JOBLETTER_SETTING_CHANGED_ENDPOINT":"/webhook/jobletter_setting_changed","NUXT_ENV_API_WEBHOOK_PAGE_VIEWED":"/webhook/page_viewed","NUXT_ENV_API_WEBHOOK_PROFILE_DATA_CHANGED_ENDPOINT":"/webhook/profile_data_changed","NUXT_ENV_API_WEBHOOK_PROFILE_DOCUMENT_CHANGED_ENDPOINT":"/webhook/profile_document_changed","NUXT_ENV_API_WEBHOOK_PROFILE_IMAGE_CHANGED_ENDPOINT":"/webhook/profile_image_changed","NUXT_ENV_API_WEBHOOK_PROFILE_INCOMPLETE_DIALOG_OPENED_ENDPOINT":"/webhook/incomplete_profile_dialog_opened","NUXT_ENV_API_WEBHOOK_PROFILE_SEARCHABLE_CHANGED_ENDPOINT":"/webhook/profile_data_changed","NUXT_ENV_API_WEBHOOK_REGISTERED_ENDPOINT":"/webhook/registered","NUXT_ENV_API_WEBHOOK_ORGANIZATION_REGISTERED_ENDPOINT":"/webhook/organization_registered","NUXT_ENV_API_WEBHOOK_SEDCARD_DOWNLOADED_ENDPOINT":"/webhook/sedcard_downloaded","NUXT_ENV_API_WEBHOOK_USER_SETTINGS_CHANGED_ENDPOINT":"/webhook/user_settings_changed","NUXT_ENV_API_WEBHOOK_USER_DELETED_ENDPOINT":"/webhook/deleted","NUXT_ENV_API_WEBHOOK_USER_PASSWORD_FORGOT":"/webhook/forgot_password","NUXT_ENV_API_WEBHOOK_ORGANIZATION_STARED":"/webhook/organization_stared","NUXT_ENV_API_WEBHOOK_ORGANIZATION_UNSTARED":"/webhook/organization_unstared","NUXT_ENV_API_WEBHOOK_PROFILE_VIDEO_CHANGED_ENDPOINT":"/webhook/profile_video_changed","NUXT_ENV_API_WEBHOOK_JOB_ALERT_SUBSCRIBED":"/webhook/job_alert_subscribed","NUXT_ENV_API_WEBHOOK_JOB_ALERT_UNSUBSCRIBED":"/webhook/job_alert_unsubscribed","NUXT_ENV_API_WEBHOOK_ORGANIZATION_VIEWED_ENDPOINT":"/webhook/organization_details_viewed","NUXT_ENV_API_WEBHOOK_ORGANIZATION_LISTING_VIEWED_ENDPOINT":"/webhook/organization_listing_viewed","NUXT_ENV_API_WEBHOOK_ORGANIZATIONS_SEARCHED_ENDPOINT":"/webhook/organizations_searched","NUXT_ENV_API_WEBHOOK_ORGANIZATION_CONTACTED":"/webhook/organization_contacted","NUXT_ENV_API_WEBHOOK_ORGANIZATION_PROJECT_REQUEST":"/webhook/project_contact","NUXT_ENV_API_WEBHOOK_GENERAL_CONTACT_REQUEST":"/webhook/general_contact","NUXT_ENV_API_WEBHOOK_EVENT_VIEWED_ENDPOINT":"/webhook/event_details_viewed","NUXT_ENV_API_WEBHOOK_EVENT_LISTING_VIEWED_ENDPOINT":"/webhook/event_listing_viewed","NUXT_ENV_TYPESENSE_API_KEY":"","NUXT_ENV_TYPESENSE_MULTISEARCH":"https://www.promotionbasis.de/search_engine/multi_search","NUXT_ENV_LEGACY_AGENCY_LOGO_URL":"/organizations/$id/logo","NUXT_ENV_MAUTIC_TRACKING_SCRIPT":"https://mailer.promotionbasis.de/mtc.js","NUXT_ENV_MAUTIC_FORMS":"https://mailer.promotionbasis.de/form/submit","NUXT_ENV_MAUTIC_FORMS_NEWSLETTER_FORM_ID":"16","NUXT_ENV_MAUTIC_FORMS_PRICELIST_FORM_ID":"17","NUXT_ENV_MAUTIC_FORMS_CONTACTFORM_FORM_ID":"20","NUXT_ENV_COOKIE_VERSION":"2","NUXT_ENV_COOKIE_LIFETIME":"90","NUXT_ENV_BASE_URL":"https://www.promotionbasis.de","NUXT_ENV_OPEN_STREET_MAPS":"https://www.promotionbasis.de/geo/nominatim","NUXT_ENV_OPEN_STREET_MAPS_TILE":"https://maps.geoapify.com/v1/tile/osm-carto/{z}/{x}/{y}.png?apiKey=4248b66428aa4b42892868a4dea035b9","NUXT_ENV_GEOAPIFY":"https://api.geoapify.com/v1/geocode/autocomplete","NUXT_ENV_GEOAPIFY_REVERSE":"https://api.geoapify.com/v1/geocode/reverse","NUXT_ENV_GEOAPIFY_BY_PLACE_ID":"https://api.geoapify.com/v2","NUXT_ENV_LEGACY_URL":"https://pm.promotionbasis.de","NUXT_ENV_LEGACY_OPEN_GRAPH_IMAGE_URL":"https://jobanbieter.promotionbasis.de/img/feeds/","NUXT_ENV_LEGACY_OPEN_GRAPH_PLAIN_IMAGE_URL":"https://jobanbieter.promotionbasis.de/img/feeds_plain/","NUXT_ENV_SPECIAL_OFFER_JOB_IDS":"13f45408-438d-4e22-a8d0-047639d364d8","NUXT_ENV_APPCAST_JOB_VIEW":"https://click.appcast.io/pixels/generic1-22395.js?ent=196","NUXT_ENV_APPCAST_JOB_APPLY_START":"https://click.appcast.io/pixels/generic2-22395.js?ent=196","NUXT_ENV_APPCAST_JOB_APPLY_FINISH":"https://click.appcast.io/pixels/generic3-22395.js?ent=196","NUXT_ENV_LOCALFORAGE_CACHE_TTL":"14400","NUXT_ENV_NUXT_COMPONENT_CACHE_MAX_AGE":"86400000","GOOGLE_API_KEY":"AIzaSyBnmBApo_MbPVEmihP7Me3gfQXpxiZQr0Y","SENTRY_DSN":"https://ba70b388758544be85cce11f5066be82@o999194.ingest.sentry.io/5977308","SCOPE":"promotionbasis","SCOPE_CANDIDATE":"promotionbasis_candidate","SCOPE_ORGANIZATION":"promotionbasis_organization","STRAPI_URL":"https://www.promotionbasis.de/cms","STRAPI_CONTENT_URL":"https://www.promotionbasis.de/dms/","STRAPI_GOOGLE_CLOUD_URL":"https://storage.googleapis.com/","MATOMO_SITE_ID":"6","HOTJAR_SITE_ID":"1518699"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
